import { compact } from "lodash";
import { useRouteMatch } from "react-router-dom";

import ProfileResponsiveTabs from "@apps/profile/profile-responsive-tabs";
import { ComplianceProgramsList } from "@apps/programs/components/compliance-programs";
import ProgramProgressPage from "@apps/programs/components/program-progress-page";
import SurveyReportPage from "@apps/reporting/components/survey-report-page";
import { isAdminVar, isManagerVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";

import EngagementSurveyHomepageActiveSurveys from "./homepage-active-surveys";
import EngagementSurveyHomepageDeliveries from "./homepage-deliveries";

enum EngagementSurveyHomepageTabs {
  Active = "Active",
  All = "All",
  Delivered = "Delivered",
  Progress = "Progress",
  Results = "Results",
}

const SURVEY_TAB_ROUTES: Record<string, string> = {
  [EngagementSurveyHomepageTabs.Active]: "/survey/active",
  [EngagementSurveyHomepageTabs.All]: "/survey/all",
  [EngagementSurveyHomepageTabs.Delivered]: "/survey/delivered",
  [EngagementSurveyHomepageTabs.Progress]: "/survey/progress",
  [EngagementSurveyHomepageTabs.Results]: "/survey/results",
};

const EngagementSurveyHomepage = () => {
  const { path } = useRouteMatch();
  const isAdmin = isAdminVar();
  const isManager = isManagerVar();
  const canManage = isAdmin || isManager;
  const link = useLink();

  const selectedTab =
    path === "/survey/delivered"
      ? EngagementSurveyHomepageTabs.Delivered
      : path === "/survey/all"
        ? EngagementSurveyHomepageTabs.All
        : path === "/survey/progress"
          ? EngagementSurveyHomepageTabs.Progress
          : path === "/survey/results"
            ? EngagementSurveyHomepageTabs.Results
            : EngagementSurveyHomepageTabs.Active;

  const tabButtons: ToggleButtonGroupType<string>[] = compact([
    {
      value: EngagementSurveyHomepageTabs.Active,
      title: "Active",
      active: selectedTab === EngagementSurveyHomepageTabs.Active,
    },
    {
      value: EngagementSurveyHomepageTabs.Delivered,
      title: "Delivered",
      active: selectedTab === EngagementSurveyHomepageTabs.Delivered,
    },
    canManage && {
      value: EngagementSurveyHomepageTabs.All,
      title: "All",
      active: selectedTab === EngagementSurveyHomepageTabs.All,
    },
    canManage && {
      value: EngagementSurveyHomepageTabs.Progress,
      title: "Progress",
      active: selectedTab === EngagementSurveyHomepageTabs.Progress,
    },
    canManage && {
      value: EngagementSurveyHomepageTabs.Results,
      title: "Results",
      active: selectedTab === EngagementSurveyHomepageTabs.Results,
    },
  ]);

  const handleTabClick = (button: ToggleButtonGroupType<string>) => {
    link.redirect(SURVEY_TAB_ROUTES[button.value ?? ""] ?? "/survey");
  };

  const heading =
    selectedTab === EngagementSurveyHomepageTabs.Delivered
      ? "Completed Surveys"
      : selectedTab === EngagementSurveyHomepageTabs.All
        ? "All Surveys"
        : "Active Surveys";

  return (
    <Layout>
      <Layout.Header
        breadcrumbs={[{ title: "Surveys", url: "/survey" }]}
        afterTitle={
          <ProfileResponsiveTabs
            buttons={tabButtons}
            onClick={handleTabClick}
            dividerAfterIndex={2}
          />
        }
      >
        {(isAdmin || isManager) && (
          <div className="flex justify-end gap-4 items-center">
            <Button
              text="Create Survey"
              to="/survey/program/new"
              theme={buttonTheme.primary}
              size={ButtonSize.medium}
            />
          </div>
        )}
      </Layout.Header>
      <Layout.Container>
        {selectedTab === EngagementSurveyHomepageTabs.Progress ? (
          <ProgramProgressPage />
        ) : selectedTab === EngagementSurveyHomepageTabs.Results ? (
          <main className="absolute inset-0 flex flex-col overflow-hidden">
            <div className="@container/layout-main mx-auto flex w-full flex-1 flex-col min-h-0">
              <div className="px-8 pt-6 pb-2">
                <div className="text-lg font-semibold text-gray-900">
                  Results
                </div>
              </div>
              <div className="relative flex-1 min-h-0">
                <SurveyReportPage embedded />
              </div>
            </div>
          </main>
        ) : (
          <Layout.Main width="default">
            <div className="text-lg font-semibold text-gray-900">{heading}</div>
            {selectedTab === EngagementSurveyHomepageTabs.Active && (
              <EngagementSurveyHomepageActiveSurveys />
            )}

            {selectedTab === EngagementSurveyHomepageTabs.All && (
              <div className="py-8">
                <ComplianceProgramsList />
              </div>
            )}

            {selectedTab === EngagementSurveyHomepageTabs.Delivered && (
              <EngagementSurveyHomepageDeliveries />
            )}
          </Layout.Main>
        )}
      </Layout.Container>
    </Layout>
  );
};

export default EngagementSurveyHomepage;
